import { createContext, useCallback, useContext, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import type { ChatMessage } from "./CortexAssistant"
import { CortexChatModal } from "./CortexAssistant"
import { ContactUsModal } from "./ContactUsModal"

type CortexModalContextValue = {
  openCortex: (initialMessages?: ChatMessage[]) => void
  openContact: () => void
  isCortexOpen: boolean
}

const CortexModalContext = createContext<CortexModalContextValue | null>(null)

export function useCortexModal(): CortexModalContextValue | null {
  return useContext(CortexModalContext)
}

export function CortexModalProvider({ children }: { children: ReactNode }) {
  const [cortexOpen, setCortexOpen] = useState(false)
  const [initialMessages, setInitialMessages] = useState<ChatMessage[]>([])
  const [contactOpen, setContactOpen] = useState(false)

  const openCortex = useCallback((messages: ChatMessage[] = []) => {
    setInitialMessages(messages)
    setCortexOpen(true)
  }, [])

  const closeCortex = useCallback(() => {
    setCortexOpen(false)
  }, [])

  const openContact = useCallback(() => {
    setContactOpen(true)
  }, [])

  const closeContact = useCallback(() => {
    setContactOpen(false)
  }, [])

  const value: CortexModalContextValue = { openCortex, openContact, isCortexOpen: cortexOpen }

  return (
    <CortexModalContext.Provider value={value}>
      {children}
      {cortexOpen &&
        createPortal(
          <CortexChatModal
            onClose={closeCortex}
            initialMessages={initialMessages}
            onOpenContact={openContact}
          />,
          document.body
        )}
      {contactOpen && createPortal(<ContactUsModal onClose={closeContact} />, document.body)}
    </CortexModalContext.Provider>
  )
}
